import {
  Bot, Braces, CloudCog, Compass, CreditCard, LayoutDashboard,
  Lightbulb, Rocket, Smartphone, Sparkles, Store, Workflow,
} from "lucide-react";

export const services = [
  {
    slug: "strategy",
    icon: Compass,
    title: "Product Discovery & Strategy",
    short: "Turn expertise and market signals into a focused product worth building.",
    items: ["AI opportunity brainstorming", "Product ikigai workshops", "Revenue model exploration", "MVP definition", "Feature prioritization", "Product roadmaps"],
  },
  {
    slug: "web",
    icon: LayoutDashboard,
    title: "Custom Web Applications",
    short: "Purpose-built software that creates leverage for your customers and your team.",
    items: ["Responsive web apps", "SaaS platforms", "Admin dashboards", "Customer portals", "E-commerce experiences", "API integrations"],
  },
  {
    slug: "mobile",
    icon: Smartphone,
    title: "iOS & Android Apps",
    short: "Mobile products designed for real habits, reliable delivery, and store readiness.",
    items: ["Mobile-first product design", "Native or cross-platform apps", "App Store and Play Store readiness", "Push notifications", "Subscriptions and payments"],
  },
  {
    slug: "agents",
    icon: Bot,
    title: "AI Agents & Automation",
    short: "Practical intelligence embedded where it can save time, improve service, or grow revenue.",
    items: ["Workflow assistants", "Lead capture agents", "Knowledge agents", "Customer support agents", "Operations automation", "AI product features"],
  },
  {
    slug: "commerce",
    icon: CreditCard,
    title: "E-commerce & Revenue Systems",
    short: "The product, payment, and lifecycle infrastructure that turns value into revenue.",
    items: ["Product catalogs", "Checkout flows", "Subscription models", "Payment integrations", "Digital product monetization", "Customer lifecycle flows"],
  },
  {
    slug: "leadership",
    icon: CloudCog,
    title: "Technical Leadership & Delivery",
    short: "Senior engineering judgment from architecture through launch and scale.",
    items: ["Architecture planning", "Agile delivery", "Cloud deployment", "API design", "DevOps and releases", "Scaling strategy"],
  },
];

export const process = [
  { n: "01", title: "Discover", text: "Clarify the user, the opportunity, the business model, and what success must mean." },
  { n: "02", title: "Design", text: "Shape the experience, validate the workflow, and turn ambiguity into a buildable plan." },
  { n: "03", title: "Build", text: "Engineer the smallest excellent version with strong foundations and visible momentum." },
  { n: "04", title: "Launch", text: "Deploy confidently, instrument the product, and prepare the market-facing experience." },
  { n: "05", title: "Iterate", text: "Learn from real use, improve what matters, and expand where the evidence points." },
];

export const capabilities = [
  { icon: Braces, title: "SaaS & platforms", text: "Multi-tenant products, portals, dashboards, and subscription experiences." },
  { icon: Smartphone, title: "Mobile experiences", text: "Customer and team apps built around the moments that matter." },
  { icon: Workflow, title: "Workflow systems", text: "Connected operations that replace friction, repetition, and spreadsheet sprawl." },
  { icon: Store, title: "Digital commerce", text: "Catalogs, memberships, payments, and monetized digital experiences." },
  { icon: Bot, title: "AI-native tools", text: "Agents, copilots, retrieval systems, and AI-enhanced product features." },
  { icon: CloudCog, title: "Cloud foundations", text: "Architecture, APIs, deployment, observability, and release operations." },
];

export const audiences = [
  "Professionals with domain expertise", "Coaches and consultants", "Small businesses",
  "Founders", "Creators", "Local service businesses", "Teams modernizing with AI",
];

export const caseStudies = [
  { type: "AI-enabled SaaS", title: "Expertise becomes a scalable product", metric: "Strategy + SaaS + AI", color: "mint" },
  { type: "Mobile experience", title: "A useful customer moment, always at hand", metric: "Product + Mobile", color: "violet" },
  { type: "Workflow automation", title: "Repetitive operations become intelligent systems", metric: "Agents + Integrations", color: "blue" },
  { type: "Commerce platform", title: "A clearer path from product value to revenue", metric: "Commerce + Lifecycle", color: "amber" },
];

export const agentUseCases = [
  { icon: Sparkles, title: "Customer-facing guidance", text: "Answer questions, qualify needs, and move customers toward the right next step." },
  { icon: Workflow, title: "Operations coordination", text: "Route requests, update systems, generate documents, and keep work moving." },
  { icon: Lightbulb, title: "Knowledge activation", text: "Make your methods, policies, and institutional knowledge easier to use." },
  { icon: Rocket, title: "Product intelligence", text: "Add recommendation, summarization, generation, and decision support to your product." },
];

export const faqs = [
  ["Do I need a fully formed product idea?", "No. A rough direction, recurring problem, area of expertise, or business goal is enough to begin. Discovery is designed to turn those signals into a focused opportunity."],
  ["Can you improve an existing product or workflow?", "Yes. We can modernize a customer experience, add AI capabilities, redesign a workflow, stabilize architecture, or help prioritize the next stage of growth."],
  ["How do projects typically begin?", "Most engagements begin with a product conversation, followed by a focused discovery phase. That gives us shared clarity on users, value, scope, risks, and the smartest first release."],
  ["Do you work with non-technical founders?", "Absolutely. We translate business context and domain knowledge into product decisions without requiring clients to speak in technical shorthand."],
  ["Can you help after launch?", "Yes. Products improve through real-world learning. We support measurement, iteration, operations, and thoughtful expansion after the first release."],
];
